import { useEffect, useRef, useState, useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { updateLead } from '../../../store/slices/leadsSlice';

import { buildLeadSubmitPayload } from '../leadFormHelpers';



const AUTOSAVE_DEBOUNCE_MS = 750;

const SAVED_INDICATOR_MS = 2500;

const SKIP_COMPARE_KEYS = new Set(['attachments']);



function cloneFormSnapshot(formData) {

  return JSON.parse(JSON.stringify(formData ?? {}));

}



function getChangedFields(previous, next) {

  if (!previous || !next) return [];

  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);

  const changed = [];



  keys.forEach((key) => {

    if (SKIP_COMPARE_KEYS.has(key)) return;

    const prevVal = previous[key];

    const nextVal = next[key];

    if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {

      changed.push(key);

    }

  });



  return changed;

}



export function useLeadAutoSave({

  enabled,

  leadId,

  formData,

  statusOptions,

  loading,

  leadLoading,

  attachmentsHydrate,
  extraPayload = {},

}) {

  const dispatch = useDispatch();

  const [autoSaving, setAutoSaving] = useState(false);

  const [autoSaved, setAutoSaved] = useState(false);



  const readyRef = useRef(false);

  const snapshotRef = useRef(null);

  const pendingFieldsRef = useRef([]);

  const timerRef = useRef(null);

  const savingRef = useRef(false);

  const savedIndicatorTimerRef = useRef(null);



  const clearSavedIndicator = useCallback(() => {

    if (savedIndicatorTimerRef.current) {

      clearTimeout(savedIndicatorTimerRef.current);

      savedIndicatorTimerRef.current = null;

    }

    setAutoSaved(false);

  }, []);



  const showSavedIndicator = useCallback(() => {

    setAutoSaved(true);

    if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);

    savedIndicatorTimerRef.current = setTimeout(() => {

      setAutoSaved(false);

      savedIndicatorTimerRef.current = null;

    }, SAVED_INDICATOR_MS);

  }, []);



  const resetReadyState = useCallback(() => {

    readyRef.current = false;

    snapshotRef.current = null;

    pendingFieldsRef.current = [];

    if (timerRef.current) {

      clearTimeout(timerRef.current);

      timerRef.current = null;

    }

    clearSavedIndicator();

  }, [clearSavedIndicator]);



  const formDataRef = useRef(formData);

  formDataRef.current = formData;



  useEffect(() => {

    if (!enabled) {

      resetReadyState();

      return undefined;

    }



    if (loading || leadLoading) {

      readyRef.current = false;

      return undefined;

    }



    const timer = setTimeout(() => {

      snapshotRef.current = cloneFormSnapshot(formDataRef.current);

      readyRef.current = true;

    }, 200);



    return () => clearTimeout(timer);

  }, [enabled, loading, leadLoading, attachmentsHydrate, resetReadyState]);



  useEffect(() => {

    if (!enabled || !leadId || !readyRef.current || !snapshotRef.current) return undefined;



    const changed = getChangedFields(snapshotRef.current, formData);

    if (!changed.length) return undefined;



    pendingFieldsRef.current = [...new Set([...pendingFieldsRef.current, ...changed])];

    clearSavedIndicator();



    if (timerRef.current) clearTimeout(timerRef.current);



    timerRef.current = setTimeout(async () => {

      const hasPendingChanges = pendingFieldsRef.current.length > 0;

      pendingFieldsRef.current = [];



      if (!hasPendingChanges || savingRef.current) return;



      savingRef.current = true;

      setAutoSaving(true);



      try {

        const payload = { ...buildLeadSubmitPayload(formData, statusOptions), ...extraPayload };

        const response = await dispatch(updateLead({ id: leadId, data: payload })).unwrap();



        if (response?.success !== false) {

          snapshotRef.current = cloneFormSnapshot(formData);

          showSavedIndicator();

        }

      } catch (_) {

        // keep editing silently

      } finally {

        savingRef.current = false;

        setAutoSaving(false);

      }

    }, AUTOSAVE_DEBOUNCE_MS);



    return () => {

      if (timerRef.current) {

        clearTimeout(timerRef.current);

        timerRef.current = null;

      }

    };

  }, [

    enabled,

    leadId,

    formData,

    statusOptions,

    dispatch,

    clearSavedIndicator,

    showSavedIndicator,

  ]);



  useEffect(() => () => resetReadyState(), [resetReadyState]);



  return { autoSaving, autoSaved, resetAutoSave: resetReadyState };

}

